import { RubyGemMark } from '../../components/RubyGemMark';
import { useEffect, useRef, useState } from 'react';
import {
  Plus, RefreshCw, Rocket, RotateCcw, Archive, Copy, Check,
  AlertTriangle, ExternalLink, Video,
} from 'lucide-react';
import type { Tutorial, TutorialVersion, TutorialStep } from '@platform/shared-types';
import {
  tutorialApi, TutorialApiError, manifestUrl, type TutorialWithVersions,
} from '../../services/tutorialApi';
import { describeBlockers, type Blocker } from './publishBlockers';
import { ConfirmDialog } from '../../components/finance/financeUi';
import SearchableDropdown from '../../components/SearchableDropdown';
import StepReviewRow from './StepReviewRow';
import { RubyLoader } from '../../components/RubyLoader';

const TRANSCRIPT_POLL_MS = 4000;
const TRANSCRIPT_POLL_MAX_ATTEMPTS = 20; // ~80s of automatic checking
const RENDER_POLL_MS = 4000;
const RENDER_POLL_MAX_ATTEMPTS = 90; // ~6 minutes -- a render is "two to five minutes" per render.ts

/**
 * A step's transcript may legitimately still be in flight: API Gateway caps
 * an integration at 29s, transcription of a multi-minute recording routinely
 * exceeds that, and the recorder's client swallows the resulting 504 and
 * carries on -- the backend keeps working and persists the result. A step
 * with a clip but no transcript AND no narration yet is exactly that
 * in-between state, not a failed recording.
 */
const looksTranscriptPending = (steps: TutorialVersion['steps']) =>
  steps.some((s) => !!s.clip && !s.transcriptRaw.trim() && !s.narration.trim());

// `== null` (loose) deliberately, not `=== null`: the schema declares tenantId
// nullable, but a value coming off the wire can arrive as `undefined` too (a
// key genuinely absent from the JSON, e.g. an older/short-lived backend
// build) -- treating only literal `null` as global would print the word
// "undefined" as if it were a real tenant id.
const isGlobalTenant = (tenantId: string | null | undefined) => tenantId == null;

const upsertVersion = (versions: TutorialVersion[], v: TutorialVersion): TutorialVersion[] =>
  (versions.some((x) => x.versionNo === v.versionNo)
    ? versions.map((x) => (x.versionNo === v.versionNo ? v : x))
    : [...versions, v]
  ).sort((a, b) => a.versionNo - b.versionNo);

const STATE_TONE: Record<Tutorial['state'], string> = {
  draft: 'bg-gray-100 text-gray-600',
  published: 'bg-emerald-100 text-emerald-700',
  retired: 'bg-red-50 text-red-500',
};

const RENDER_TONE: Record<TutorialVersion['render']['state'], string> = {
  none: 'bg-gray-100 text-gray-500',
  queued: 'bg-amber-100 text-amber-700',
  running: 'bg-amber-100 text-amber-700',
  ready: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
};

export default function TutorialsTab() {
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [viewTenantId, setViewTenantId] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TutorialWithVersions | null>(null);
  const [draft, setDraft] = useState<TutorialVersion | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [blockerMsgs, setBlockerMsgs] = useState<string[] | null>(null);

  const [renderPolling, setRenderPolling] = useState(false);
  const [transcriptPending, setTranscriptPending] = useState(false);
  const [transcriptAutoChecking, setTranscriptAutoChecking] = useState(false);

  const [manifestCopied, setManifestCopied] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newTenantId, setNewTenantId] = useState('');
  const [newSurface, setNewSurface] = useState<'platform-app' | 'pos'>('platform-app');

  const [showRetireConfirm, setShowRetireConfirm] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
  const [reRecordConfirm, setReRecordConfirm] = useState<{ stepId: string; kind: 'audio' | 'clip' } | null>(null);

  // Bumped on every selectTutorial call -- every in-flight poll checks this
  // before touching state, so switching tutorials mid-poll can never let a
  // stale response overwrite the newly-selected one's data.
  const selectionRef = useRef(0);
  const renderTimerRef = useRef<number | null>(null);
  const transcriptTimerRef = useRef<number | null>(null);

  const stopTimers = () => {
    if (renderTimerRef.current) { window.clearTimeout(renderTimerRef.current); renderTimerRef.current = null; }
    if (transcriptTimerRef.current) { window.clearTimeout(transcriptTimerRef.current); transcriptTimerRef.current = null; }
  };
  useEffect(() => stopTimers, []);

  const loadTutorials = async (tenantId?: string) => {
    setLoading(true); setListError('');
    try {
      const { tutorials: rows } = await tutorialApi.list(tenantId || undefined);
      setTutorials(rows);
    } catch (e: any) {
      setListError(e.message || 'Could not load tutorials.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void loadTutorials(); }, []);

  const pollRenderStatus = (id: string, gen: number, attempt: number) => {
    renderTimerRef.current = window.setTimeout(async () => {
      if (gen !== selectionRef.current) return;
      try {
        const { draft: fresh } = await tutorialApi.getDraft(id);
        if (gen !== selectionRef.current) return;
        setDraft(fresh);
        const stillWorking = fresh.render.state === 'queued' || fresh.render.state === 'running';
        if (stillWorking && attempt + 1 < RENDER_POLL_MAX_ATTEMPTS) {
          pollRenderStatus(id, gen, attempt + 1);
        } else {
          setRenderPolling(false);
        }
      } catch {
        if (attempt + 1 < RENDER_POLL_MAX_ATTEMPTS) pollRenderStatus(id, gen, attempt + 1);
        else setRenderPolling(false);
      }
    }, RENDER_POLL_MS);
  };

  const maybeStartTranscriptPoll = (v: TutorialVersion, id: string, gen: number, attempt = 0) => {
    const pending = looksTranscriptPending(v.steps);
    setTranscriptPending(pending);
    if (!pending || attempt >= TRANSCRIPT_POLL_MAX_ATTEMPTS) {
      setTranscriptAutoChecking(false);
      return;
    }
    setTranscriptAutoChecking(true);
    transcriptTimerRef.current = window.setTimeout(async () => {
      if (gen !== selectionRef.current) return;
      try {
        const { draft: fresh } = await tutorialApi.getDraft(id);
        if (gen !== selectionRef.current) return;
        setDraft(fresh);
        maybeStartTranscriptPoll(fresh, id, gen, attempt + 1);
      } catch {
        if (gen === selectionRef.current) maybeStartTranscriptPoll(v, id, gen, attempt + 1);
      }
    }, TRANSCRIPT_POLL_MS);
  };

  const checkTranscriptNow = () => {
    if (!draft || !selectedId) return;
    stopTimers();
    maybeStartTranscriptPoll(draft, selectedId, selectionRef.current, 0);
  };

  const selectTutorial = async (id: string) => {
    selectionRef.current += 1;
    const gen = selectionRef.current;
    stopTimers();
    setRenderPolling(false); setTranscriptPending(false); setTranscriptAutoChecking(false);
    setSelectedId(id);
    setDetail(null); setDraft(null); setDetailError(''); setBlockerMsgs(null); setActionError('');
    setDetailLoading(true);
    try {
      const [d, dr] = await Promise.all([tutorialApi.get(id), tutorialApi.getDraft(id)]);
      if (gen !== selectionRef.current) return;
      setDetail(d);
      setDraft(dr.draft);
      maybeStartTranscriptPoll(dr.draft, id, gen, 0);
      if (dr.draft.render.state === 'queued' || dr.draft.render.state === 'running') {
        setRenderPolling(true);
        pollRenderStatus(id, gen, 0);
      }
    } catch (e: any) {
      if (gen !== selectionRef.current) return;
      setDetailError(e.message || 'Could not load this tutorial.');
    } finally {
      if (gen === selectionRef.current) setDetailLoading(false);
    }
  };

  /**
   * `saveDraft` replaces the WHOLE steps array (same shape as academyApi's
   * saveModule), so two overlapping edits -- an Approve click right after a
   * narration blur, say -- would otherwise race: whichever PUT lands last
   * wins and silently discards the other. CurriculumTab.tsx solves the exact
   * same class of bug with a serialised write queue plus a ref holding the
   * latest data (never the render closure's possibly-stale snapshot) --
   * mirrored here rather than reinvented.
   */
  const draftRef = useRef<TutorialVersion | null>(null);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  const writeQueue = useRef<Promise<unknown>>(Promise.resolve());
  const enqueueWrite = <T,>(fn: () => Promise<T>): Promise<T> => {
    const run = writeQueue.current.then(fn, fn);
    writeQueue.current = run.catch(() => undefined);
    return run;
  };

  const patchStep = async (stepId: string, patch: Partial<TutorialStep>) => {
    if (!selectedId) return;
    // Optimistic UI, applied (and mirrored into the ref synchronously, not
    // just via the effect above) before the write is even queued, so a
    // second patch fired in the same tick already sees this one.
    setDraft((d) => {
      if (!d) return d;
      const next = { ...d, steps: d.steps.map((s) => (s.stepId === stepId ? { ...s, ...patch } : s)) };
      draftRef.current = next;
      return next;
    });
    setActionBusy(true); setActionError('');
    try {
      await enqueueWrite(async () => {
        const latest = draftRef.current;
        if (!latest) return;
        const { draft: saved } = await tutorialApi.saveDraft(selectedId, latest.steps);
        setDraft(saved);
        draftRef.current = saved;
      });
    } catch (e: any) {
      setActionError(e.message || 'That change did not save.');
      try {
        const { draft: fresh } = await tutorialApi.getDraft(selectedId);
        setDraft(fresh);
        draftRef.current = fresh;
      } catch { /* keep the optimistic state visible rather than blank the screen */ }
    } finally {
      setActionBusy(false);
    }
  };

  const confirmReRecord = async () => {
    if (!reRecordConfirm) return;
    const { stepId, kind } = reRecordConfirm;
    setReRecordConfirm(null);
    // Neither action can actually capture new audio/video from super-admin-ui
    // -- the recorder (mic + click-stream capture) lives in platform-app.
    // This clears the stale artifact so the step is unambiguously incomplete
    // again; the author redoes it from the recorder overlay and this screen
    // picks it up on its next refresh/poll.
    const patch: Partial<TutorialStep> = kind === 'clip'
      ? { clip: null }
      : { micKey: null, transcriptRaw: '', narration: '', narrationApproved: false };
    await patchStep(stepId, patch);
  };

  const runRender = async () => {
    if (!selectedId) return;
    setActionBusy(true); setActionError(''); setBlockerMsgs(null);
    try {
      const { render } = await tutorialApi.render(selectedId);
      setDraft((d) => (d ? { ...d, render } : d));
      setRenderPolling(true);
      pollRenderStatus(selectedId, selectionRef.current, 0);
    } catch (e: any) {
      setActionError(e.message || 'Could not start the render.');
    } finally {
      setActionBusy(false);
    }
  };

  const runPublish = async () => {
    if (!selectedId) return;
    setActionBusy(true); setActionError(''); setBlockerMsgs(null);
    try {
      const result = await tutorialApi.publish(selectedId);
      setDetail((d) => (d ? { tutorial: result.tutorial, versions: upsertVersion(d.versions, result.version) } : d));
      setTutorials((prev) => prev.map((t) => (t.tutorialId === result.tutorial.tutorialId ? result.tutorial : t)));
      const { draft: fresh } = await tutorialApi.getDraft(selectedId);
      setDraft(fresh);
    } catch (e: any) {
      if (e instanceof TutorialApiError && e.blockers?.length) {
        setBlockerMsgs(describeBlockers(e.blockers as Blocker[]));
      } else {
        setActionError(e.message || 'Could not publish.');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const runRollback = async () => {
    if (!selectedId || !rollbackTarget) return;
    setActionBusy(true); setActionError('');
    try {
      const result = await tutorialApi.rollback(selectedId, Number(rollbackTarget));
      setDetail((d) => (d ? { tutorial: result.tutorial, versions: d.versions } : d));
      setTutorials((prev) => prev.map((t) => (t.tutorialId === result.tutorial.tutorialId ? result.tutorial : t)));
      setShowRollbackModal(false);
      setRollbackTarget(null);
    } catch (e: any) {
      setActionError(e.message || 'Could not roll back.');
    } finally {
      setActionBusy(false);
    }
  };

  const runRetire = async () => {
    if (!selectedId) return;
    setActionBusy(true); setActionError('');
    try {
      const { tutorial } = await tutorialApi.retire(selectedId);
      setDetail((d) => (d ? { ...d, tutorial } : d));
      setTutorials((prev) => prev.map((t) => (t.tutorialId === tutorial.tutorialId ? tutorial : t)));
      setShowRetireConfirm(false);
    } catch (e: any) {
      setActionError(e.message || 'Could not retire this tutorial.');
    } finally {
      setActionBusy(false);
    }
  };

  const runCreate = async () => {
    if (!newSlug.trim() || !newTitle.trim()) return;
    setActionBusy(true); setActionError('');
    try {
      const { tutorial } = await tutorialApi.create(
        { slug: newSlug.trim(), title: newTitle.trim(), surface: newSurface },
        newTenantId.trim() || undefined,
      );
      setTutorials((prev) => [...prev, tutorial]);
      setShowCreate(false);
      setNewSlug(''); setNewTitle(''); setNewTenantId(''); setNewSurface('platform-app');
      void selectTutorial(tutorial.tutorialId);
    } catch (e: any) {
      setActionError(e.message || 'Could not create the tutorial.');
    } finally {
      setActionBusy(false);
    }
  };

  const copyManifest = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setManifestCopied(true);
      window.setTimeout(() => setManifestCopied(false), 2000);
    } catch { /* clipboard unavailable -- the URL is still visible to select by hand */ }
  };

  const tutorial = detail?.tutorial;
  const versions = detail?.versions ?? [];
  const publishedVersion = tutorial?.publishedVersionNo
    ? versions.find((v) => v.versionNo === tutorial.publishedVersionNo) ?? null
    : null;
  const rollbackOptions = versions
    .filter((v) => v.status === 'published' && v.render.state === 'ready' && v.versionNo !== tutorial?.publishedVersionNo)
    .sort((a, b) => b.versionNo - a.versionNo)
    .map((v) => ({
      value: String(v.versionNo),
      label: `Version ${v.versionNo}`,
      subtitle: v.publishedAt ? new Date(v.publishedAt).toLocaleString() : undefined,
    }));

  const canPublish = !!draft && draft.render.state === 'ready';

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr] items-start">
      {/* Tutorial list */}
      <section className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Tutorials</h2>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700 transition"
          >
            <Plus className="w-3.5 h-3.5" />
            Tutorial
          </button>
        </div>

        <div className="p-3 border-b border-gray-200 space-y-2">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            View as tenant (optional)
          </label>
          <div className="flex gap-1.5">
            <input
              value={viewTenantId}
              onChange={(e) => setViewTenantId(e.target.value)}
              placeholder="Tenant id — blank sees everything"
              className="flex-1 min-h-[44px] rounded-lg border border-gray-300 px-2.5 text-xs text-gray-900 focus:border-gray-900 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => void loadTutorials(viewTenantId.trim())}
              className="min-h-[44px] px-3 rounded-lg text-xs font-semibold text-gray-600 border border-gray-300 hover:border-gray-900 hover:text-gray-900 transition"
            >
              Go
            </button>
          </div>
        </div>

        {listError && <p className="text-sm text-red-600 px-4 py-3">{listError}</p>}
        {loading && <RubyLoader variant="section" label="Loading tutorials…" />}

        <div className="p-2 space-y-1 max-h-[70vh] overflow-y-auto">
          {!loading && tutorials.length === 0 && (
            <p className="text-sm text-gray-500 p-3">No tutorials yet.</p>
          )}
          {tutorials.map((t) => {
            const on = selectedId === t.tutorialId;
            return (
              <button
                key={t.tutorialId}
                type="button"
                onClick={() => void selectTutorial(t.tutorialId)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition ${on ? 'bg-gray-900 text-white' : 'hover:bg-gray-100'}`}
              >
                <p className={`text-sm font-semibold truncate ${on ? 'text-white' : 'text-gray-900'}`}>{t.title}</p>
                <p className={`text-xs truncate ${on ? 'text-white/60' : 'text-gray-500'}`}>/{t.slug}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${on ? 'bg-white/15 text-white' : STATE_TONE[t.state]}`}>
                    {t.state}
                  </span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${on ? 'bg-white/15 text-white' : isGlobalTenant(t.tenantId) ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                    {isGlobalTenant(t.tenantId) ? 'Global' : `Tenant: ${t.tenantId}`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Detail */}
      <section className="space-y-4">
        {!selectedId && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-sm">Pick a tutorial to review its narration.</p>
          </div>
        )}

        {detailLoading && <RubyLoader variant="section" label="Loading…" />}
        {detailError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{detailError}</p>
        )}

        {tutorial && draft && (
          <>
            <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{tutorial.title}</h2>
                  <p className="text-xs text-gray-500 font-mono">/{tutorial.slug} · {tutorial.surface}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${STATE_TONE[tutorial.state]}`}>
                    {tutorial.state}
                  </span>
                  <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${isGlobalTenant(tutorial.tenantId) ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                    {isGlobalTenant(tutorial.tenantId) ? 'Global' : `Tenant: ${tutorial.tenantId}`}
                  </span>
                </div>
              </div>

              {tutorial.publishedVersionNo && (
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <ExternalLink className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-xs font-mono text-gray-600 truncate flex-1">
                    {manifestUrl(tutorial.tenantId, tutorial.slug)}
                  </span>
                  <button
                    type="button"
                    onClick={() => void copyManifest(manifestUrl(tutorial.tenantId, tutorial.slug))}
                    className="inline-flex items-center gap-1 min-h-[36px] px-2.5 rounded-md text-[11px] font-semibold text-gray-600 border border-gray-300 hover:border-gray-900 hover:text-gray-900 transition flex-shrink-0"
                  >
                    {manifestCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {manifestCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => void runRender()}
                  disabled={actionBusy || renderPolling || tutorial.state === 'retired'}
                  className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700 transition disabled:opacity-40"
                >
                  {renderPolling ? <RubyGemMark size={14} drawing color="currentColor" /> : <Video className="w-3.5 h-3.5" />}
                  {renderPolling ? 'Rendering…' : 'Render draft'}
                </button>

                <button
                  type="button"
                  onClick={() => void runPublish()}
                  disabled={actionBusy || !canPublish || tutorial.state === 'retired'}
                  title={!canPublish ? 'Render this version before publishing' : undefined}
                  className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-40"
                >
                  <Rocket className="w-3.5 h-3.5" />
                  Publish
                </button>

                {rollbackOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowRollbackModal(true)}
                    disabled={actionBusy}
                    className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-lg text-xs font-semibold text-gray-600 border border-gray-300 hover:border-gray-900 hover:text-gray-900 transition disabled:opacity-40"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Roll back
                  </button>
                )}

                {tutorial.state !== 'retired' && (
                  <button
                    type="button"
                    onClick={() => setShowRetireConfirm(true)}
                    disabled={actionBusy}
                    className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-lg text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition disabled:opacity-40"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    Retire
                  </button>
                )}
              </div>

              {blockerMsgs && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-1">
                  <p className="text-xs font-bold text-amber-900">Not ready to publish:</p>
                  <ul className="text-xs text-amber-800 list-disc list-inside space-y-0.5">
                    {blockerMsgs.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}
              {actionError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{actionError}</p>
              )}
              {transcriptPending && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {transcriptAutoChecking
                    ? <RubyGemMark size={14} drawing />
                    : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span className="flex-1">
                    {transcriptAutoChecking
                      ? 'Transcript still arriving for one or more steps — checking automatically. Long recordings can take a few minutes.'
                      : 'Still no transcript for one or more steps. If the recording was long, it may still be processing.'}
                  </span>
                  <button
                    type="button"
                    onClick={checkTranscriptNow}
                    className="inline-flex items-center gap-1 min-h-[32px] px-2 rounded-md font-semibold hover:bg-amber-100 transition flex-shrink-0"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Check now
                  </button>
                </div>
              )}
            </div>

            {/* Draft / Published side by side -- the published version keeps
                serving no matter what happens to the draft below it. */}
            <div className="grid gap-4 xl:grid-cols-2 items-start">
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900">Draft — version {draft.versionNo}</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${RENDER_TONE[draft.render.state]}`}>
                    render: {draft.render.state}
                  </span>
                </div>
                {draft.render.state === 'failed' && draft.render.error && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {draft.render.error}
                  </p>
                )}
                <div className="space-y-2">
                  {[...draft.steps].sort((a, b) => a.order - b.order).map((s) => (
                    <StepReviewRow
                      key={s.stepId}
                      step={s}
                      displayNumber={s.order + 1}
                      transcriptPending={transcriptPending}
                      busy={actionBusy}
                      onNarrationChange={(narration) => void patchStep(s.stepId, { narration })}
                      onToggleApproved={() => void patchStep(s.stepId, { narrationApproved: !s.narrationApproved })}
                      onReRecordAudio={() => setReRecordConfirm({ stepId: s.stepId, kind: 'audio' })}
                      onReRecordClip={() => setReRecordConfirm({ stepId: s.stepId, kind: 'clip' })}
                    />
                  ))}
                  {draft.steps.length === 0 && (
                    <p className="text-sm text-gray-500 p-3">No steps recorded yet.</p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-3">
                <h3 className="text-sm font-bold text-gray-900">Published</h3>
                {!publishedVersion ? (
                  <p className="text-sm text-gray-500">Not published yet — publish the draft to go live.</p>
                ) : (
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-700">
                      <span className="font-semibold">Version {publishedVersion.versionNo}</span>
                      {publishedVersion.publishedAt && (
                        <span className="text-gray-400"> · published {new Date(publishedVersion.publishedAt).toLocaleString()}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {publishedVersion.steps.length} step{publishedVersion.steps.length === 1 ? '' : 's'}
                      {publishedVersion.render.durationMs ? ` · ${Math.round(publishedVersion.render.durationMs / 1000)}s` : ''}
                    </p>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      This keeps serving to every viewer while the draft above is edited, rendered and
                      re-reviewed — publishing only repoints the manifest once the new version is ready.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {/* Create tutorial */}
      <ConfirmDialog
        open={showCreate}
        title="New tutorial"
        confirmLabel="Create"
        busy={actionBusy}
        blocked={!newSlug.trim() || !newTitle.trim() ? 'Slug and title are required' : null}
        onConfirm={() => void runCreate()}
        onClose={() => setShowCreate(false)}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Title</label>
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full min-h-[44px] rounded-lg border border-gray-300 px-3 text-sm focus:border-gray-900 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Slug</label>
            <input
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="lowercase-kebab-case"
              className="w-full min-h-[44px] rounded-lg border border-gray-300 px-3 text-sm font-mono focus:border-gray-900 focus:outline-none"
            />
          </div>
          <SearchableDropdown
            label="Surface"
            options={[{ value: 'platform-app', label: 'Platform app' }, { value: 'pos', label: 'POS' }]}
            value={newSurface}
            onChange={(v) => setNewSurface((v as 'platform-app' | 'pos') ?? 'platform-app')}
          />
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Tenant id (optional)</label>
            <input
              value={newTenantId}
              onChange={(e) => setNewTenantId(e.target.value)}
              placeholder="Blank creates a Global tutorial"
              className="w-full min-h-[44px] rounded-lg border border-gray-300 px-3 text-sm focus:border-gray-900 focus:outline-none"
            />
          </div>
        </div>
      </ConfirmDialog>

      {/* Retire */}
      <ConfirmDialog
        open={showRetireConfirm}
        title="Retire this tutorial?"
        tone="danger"
        confirmLabel="Retire"
        busy={actionBusy}
        onConfirm={() => void runRetire()}
        onClose={() => setShowRetireConfirm(false)}
      >
        <p>
          This takes it off the "current" list but never deletes it — the published video stays
          reachable at its manifest URL, since it may already be embedded in a landing page or a
          shipped app build.
        </p>
      </ConfirmDialog>

      {/* Rollback */}
      <ConfirmDialog
        open={showRollbackModal}
        title="Roll back the live version"
        confirmLabel="Roll back"
        busy={actionBusy}
        blocked={rollbackTarget ? null : 'Choose a version first'}
        onConfirm={() => void runRollback()}
        onClose={() => { setShowRollbackModal(false); setRollbackTarget(null); }}
      >
        <p className="text-xs text-gray-500">
          Repoints the manifest at a version that was already published and rendered. It does not
          touch the current draft.
        </p>
        <SearchableDropdown
          label="Version"
          options={rollbackOptions}
          value={rollbackTarget}
          onChange={setRollbackTarget}
          placeholder="Choose a version…"
        />
      </ConfirmDialog>

      {/* Re-record */}
      <ConfirmDialog
        open={!!reRecordConfirm}
        title={reRecordConfirm?.kind === 'clip' ? 'Re-record this step’s clip?' : 'Re-record this step’s audio?'}
        tone="danger"
        confirmLabel="Clear it"
        busy={actionBusy}
        onConfirm={() => void confirmReRecord()}
        onClose={() => setReRecordConfirm(null)}
      >
        <p>
          {reRecordConfirm?.kind === 'clip'
            ? 'Clears the recorded clip for this step. It will block publishing until re-recorded from the tutorial recorder in platform-app.'
            : 'Clears the transcript and narration for this step (the clip is kept). Re-record the audio from the tutorial recorder in platform-app, then reopen this draft.'}
        </p>
      </ConfirmDialog>
    </div>
  );
}
